// Settings > Account (admins only): send a message that pops up as a window
// for everyone online, or for named players (POST /api/admin/broadcast).
import { useState, type FormEvent } from "react";
import { AdminBroadcastBodySchema, NOTICE_MESSAGE_MAX, NOTICE_TARGETS_MAX, NOTICE_TITLE_MAX, type AdminBroadcastResponse } from "@wp/shared";
import { api } from "../../net/api";

type Audience = "all" | "players";
type Status = { kind: "ok" | "error"; text: string } | null;

/** "Merlin, Morgana  Gandalf" -> ["Merlin", "Morgana", "Gandalf"] (commas or line breaks; spaces too, unless commas are used). */
export function parseUsernames(text: string): string[] {
  const parts = /[,;\n]/.test(text) ? text.split(/[,;\n]+/) : text.split(/\s+/);
  return parts.map((s) => s.trim()).filter(Boolean);
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

function resultText(r: AdminBroadcastResponse, audience: Audience): string {
  const parts = [`Delivered to ${plural(r.delivered, "player", "players")} online.`];
  if (audience === "players") {
    if (r.notFound.length > 0) parts.push(`No account: ${r.notFound.join(", ")}.`);
    if (r.offline.length > 0) parts.push(`Not online (they see it if they sign in within a day): ${r.offline.join(", ")}.`);
  }
  return parts.join(" ");
}

export function AdminBroadcast() {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [audience, setAudience] = useState<Audience>("all");
  const [names, setNames] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<Status>(null);

  const usernames = parseUsernames(names);
  const ready = message.trim() !== "" && (audience === "all" || usernames.length > 0);

  async function send(e: FormEvent) {
    e.preventDefault();
    if (audience === "players" && usernames.length > NOTICE_TARGETS_MAX) {
      return setStatus({ kind: "error", text: `Name at most ${NOTICE_TARGETS_MAX} players at a time.` });
    }
    const body = AdminBroadcastBodySchema.safeParse({
      ...(title.trim() ? { title } : {}),
      message,
      to: audience === "all" ? "all" : usernames,
    });
    if (!body.success) return setStatus({ kind: "error", text: body.error.issues[0]?.message ?? "Invalid message." });
    setBusy(true);
    const res = await api.adminBroadcast(body.data);
    setBusy(false);
    if (!res.ok) return setStatus({ kind: "error", text: res.error.message });
    setStatus({ kind: res.value.delivered > 0 || audience === "all" ? "ok" : "error", text: resultText(res.value, audience) });
    if (res.value.notFound.length === 0) {
      setMessage("");
      setTitle("");
    }
  }

  return (
    <form className="stack-form group-box admin-broadcast" onSubmit={send} noValidate data-testid="admin-broadcast">
      <h3>Admin: send a message to players</h3>
      <p className="muted small">It pops up as a window on their screen, in the menu or at a table.</p>
      <div className="segmented" role="radiogroup" aria-label="Recipients">
        {(
          [
            ["all", "Everyone online"],
            ["players", "Specific players"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={audience === id}
            className={audience === id ? "seg active" : "seg"}
            onClick={() => setAudience(id)}
            data-testid={`broadcast-to-${id}`}
          >
            {label}
          </button>
        ))}
      </div>
      {audience === "players" && (
        <label className="field">
          <span>Usernames (separated by commas or spaces, up to {NOTICE_TARGETS_MAX})</span>
          <input value={names} onChange={(e) => setNames(e.target.value)} placeholder="Merlin, Morgana" data-testid="broadcast-usernames" />
        </label>
      )}
      <label className="field">
        <span>Title (optional)</span>
        <input
          value={title}
          maxLength={NOTICE_TITLE_MAX}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Message from the Wizard Poker team"
          data-testid="broadcast-title"
        />
      </label>
      <label className="field">
        <span>Message</span>
        <textarea
          rows={4}
          maxLength={NOTICE_MESSAGE_MAX}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          data-testid="broadcast-message"
        />
      </label>
      <div className="feedback-foot">
        <span className="feedback-count" aria-live="polite">
          {message.length}/{NOTICE_MESSAGE_MAX}
        </span>
        <button type="submit" className="btn primary" disabled={busy || !ready} data-testid="broadcast-send">
          {busy ? "Sending..." : "Send"}
        </button>
      </div>
      {status && (
        <p className={status.kind === "ok" ? "form-ok" : "form-error"} role={status.kind === "ok" ? "status" : "alert"} data-testid="broadcast-status">
          {status.text}
        </p>
      )}
    </form>
  );
}
